// SPDX-FileCopyrightText: 2026 Ben Richardson
// SPDX-License-Identifier: AGPL-3.0-or-later

import './styles.css';
import { initTooltip } from './components/tooltip';
import { el, errorState, initGlossary, select } from './components/ui';
import { loadBundle, type Bundle } from './data';
import { escapeHtml } from './format';
import { openAbout } from './about';
import { getState, initState, setState, subscribe } from './state';
import { isView, VIEWS } from './router';
import { renderAverage } from './views/average';
import { renderLedger } from './views/ledger';
import { renderMap } from './views/map';
import { renderPropagation } from './views/propagation';
import { renderClock } from './views/clock';
import { renderRose } from './views/rose';
import { renderLabels } from './views/labels';
import { renderRecord } from './views/record';

const RENDERERS: Record<string, (ctx: { bundle: Bundle; container: HTMLElement }) => void | Promise<void>> = {
  average: renderAverage,
  ledger: renderLedger,
  map: renderMap,
  propagation: renderPropagation,
  clock: renderClock,
  rose: renderRose,
  labels: renderLabels,
  record: renderRecord,
};

const app = document.getElementById('app') as HTMLElement;

function boot(): void {
  app.innerHTML = `
    <div class="boot">
      <div class="boot-inner">
        <p class="boot-title">Air Episodes</p>
        <p class="boot-sub">Reading the hourly record for New South Wales, Queensland and the ACT…</p>
        <div class="boot-bar" role="progressbar" aria-label="Loading"><span></span></div>
      </div>
    </div>`;
}

function footer(bundle: Bundle): HTMLElement {
  const sources = Object.values(bundle.meta.sources)
    .map(
      (source) =>
        `<a href="${source.url}" target="_blank" rel="noopener">${escapeHtml(source.dataset)}</a> (${escapeHtml(
          source.publisher
        )}, <a href="${source.licenceUrl}" target="_blank" rel="noopener">${escapeHtml(source.licence)}</a>)`
    )
    .join(' · ');
  const node = el('footer', 'site-footer');
  const inner = el('div', 'footer-inner');
  // CC BY is a condition of use, not a courtesy. This attribution is asserted by a test so a
  // future edit cannot quietly drop it.
  inner.append(
    el(
      'div',
      'footer-sources',
      `<strong>Data:</strong> ${sources}. Concentrations are as measured and are not adjusted for exceptional-event determinations.`
    )
  );
  inner.append(
    el(
      'div',
      'footer-credit',
      'Built by <a href="https://benrichardson.dev/">benrichardson.dev</a> · <a href="https://lab.benrichardson.dev" target="_blank" rel="noopener">more tools &amp; sites</a>'
    )
  );
  node.append(inner);
  return node;
}

function header(bundle: Bundle): HTMLElement {
  const state = getState();
  const node = el('header', 'site-header');
  const inner = el('div', 'header-inner');

  const wordmark = el('a', 'wordmark');
  (wordmark as HTMLAnchorElement).href = '#/average';
  wordmark.innerHTML =
    '<span class="wordmark-mark" aria-hidden="true"></span><span class="wordmark-text">Air Episodes</span>';
  inner.append(wordmark);

  const controls = el('div', 'header-controls');

  const stations = [...bundle.stations].sort(
    (a, b) => a.jurisdiction.localeCompare(b.jurisdiction) || a.name.localeCompare(b.name)
  );
  controls.append(
    select(
      'Station',
      stations.map((station) => ({
        value: station.id,
        label: `${station.name} (${station.jurisdiction})`,
      })),
      state.station ?? stations[0].id,
      (value) => setState({ station: value })
    )
  );

  controls.append(
    select(
      'Pollutant',
      [
        { value: 'pm2.5', label: 'PM2.5' },
        { value: 'pm10', label: 'PM10' },
      ],
      state.pollutant,
      (value) => setState({ pollutant: value as 'pm2.5' | 'pm10' }),
      'select-field-narrow'
    )
  );

  const about = el('button', 'icon-button');
  about.type = 'button';
  about.setAttribute('aria-label', 'About this site and its data');
  about.setAttribute('data-tip', 'What this site is, where the data comes from, and what it cannot tell you');
  about.innerHTML = '<span aria-hidden="true">?</span>';
  about.addEventListener('click', () => openAbout(bundle.meta));
  controls.append(about);

  inner.append(controls);
  node.append(inner);

  const nav = el('nav', 'site-nav');
  nav.setAttribute('aria-label', 'Views');
  const navInner = el('div', 'nav-inner');
  for (const view of VIEWS) {
    const tab = el('a', `nav-tab${view.id === state.view ? ' nav-tab-on' : ''}`);
    (tab as HTMLAnchorElement).href = `#/${view.id}`;
    tab.textContent = view.label;
    tab.setAttribute('data-tip', view.blurb);
    if (view.id === state.view) tab.setAttribute('aria-current', 'page');
    navInner.append(tab);
  }
  nav.append(navInner);
  node.append(nav);
  return node;
}

async function render(bundle: Bundle): Promise<void> {
  const state = getState();
  if (!isView(state.view)) setState({ view: 'average' }, { silent: true });

  app.innerHTML = '';
  app.append(
    (() => {
      const skip = el('a', 'skip-link');
      (skip as HTMLAnchorElement).href = '#main';
      skip.textContent = 'Skip to content';
      return skip;
    })()
  );
  app.append(header(bundle));

  const main = el('main', 'main-content');
  main.id = 'main';
  app.append(main);
  app.append(footer(bundle));

  const renderer = RENDERERS[getState().view] ?? renderAverage;
  try {
    await renderer({ bundle, container: main });
  } catch {
    main.innerHTML = '';
    main.append(errorState('That view could not be drawn.', () => void render(bundle)));
  }
}

async function start(): Promise<void> {
  boot();
  initTooltip();
  initGlossary();
  try {
    const bundle = await loadBundle();
    const firstStation =
      [...bundle.stations].sort((a, b) => (b.qualifyingYears ?? 0) - (a.qualifyingYears ?? 0))[0];
    initState({
      station: firstStation?.id ?? null,
      episode: bundle.episodes[0]?.id ?? null,
    });
    // A station remembered from a previous visit may have been retired out of the dataset since.
    if (!bundle.stations.some((station) => station.id === getState().station)) {
      setState({ station: firstStation?.id ?? null }, { silent: true });
    }
    if (!bundle.episodes.some((episode) => episode.id === getState().episode)) {
      setState({ episode: bundle.episodes[0]?.id ?? null }, { silent: true });
    }
    subscribe(() => void render(bundle));
    await render(bundle);
  } catch {
    app.innerHTML = '';
    app.append(errorState('The air quality data could not be loaded.', () => void start()));
  }
}

void start();
